import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  TextInput,
  Button,
  Stack,
  Text,
  PinInput,
  Anchor,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { forgotPassword, resetPassword } from '../../store/slices/authSlice';

export const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<'email' | 'otp' | 'newPassword'>('email');
  const [email, setEmail] = useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading } = useAppSelector((state) => state.auth);

  const emailForm = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
    },
  });

  const passwordForm = useForm({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validate: {
      password: (value) => (value.length < 6 ? 'Password must be at least 6 characters' : null),
      confirmPassword: (value, values) => 
        value !== values.password ? 'Passwords do not match' : null,
    },
  });

  const [otpValue, setOtpValue] = useState('');

  const handleEmailSubmit = async (values: typeof emailForm.values) => {
    const result = await dispatch(forgotPassword(values.email));
    if (forgotPassword.fulfilled.match(result)) {
      setEmail(values.email);
      setStep('otp');
    }
  };

  const handleOtpSubmit = async (otp: string) => {
    setOtpValue(otp);
    setStep('newPassword');
  };

  const handlePasswordSubmit = async (values: typeof passwordForm.values) => {
    const result = await dispatch(resetPassword({
      email,
      otpCode: otpValue,
      newPassword: values.password,
    }));
    if (resetPassword.fulfilled.match(result)) {
      navigate('/admin/login');
    }
  };

  return (
    <Container size="xs" py={50}>
      <Paper radius="lg" p="xl" withBorder shadow="md">
        <Title ta="center" order={2} mb="md">
          Reset Password
        </Title>

        {step === 'email' && (
          <form onSubmit={emailForm.onSubmit(handleEmailSubmit)}>
            <Stack>
              <TextInput
                label="Email"
                placeholder="Enter your registered email"
                {...emailForm.getInputProps('email')}
                required
              />
              <Button type="submit" color="exportGreen" loading={loading}>
                Send Reset Code
              </Button>
              <Anchor 
                component="button" 
                type="button" 
                onClick={() => navigate('/admin/login')}
                size="sm"
                ta="center"
              >
                Back to Login
              </Anchor>
            </Stack>
          </form>
        )}

        {step === 'otp' && (
          <Stack align="center" gap="lg">
            <Text size="sm" ta="center">
              Enter the 6-digit code sent to<br />
              <Text span fw={600}>{email}</Text>
            </Text>
            <PinInput
              length={6}
              type="number"
              onComplete={handleOtpSubmit}
              disabled={loading}
            />
            <Button
              variant="subtle"
              onClick={() => setStep('email')}
              size="sm"
            >
              Back
            </Button>
          </Stack>
        )}

        {step === 'newPassword' && (
          <form onSubmit={passwordForm.onSubmit(handlePasswordSubmit)}>
            <Stack>
              <TextInput
                label="New Password"
                type="password"
                placeholder="Enter new password"
                {...passwordForm.getInputProps('password')}
                required
              />
              <TextInput
                label="Confirm Password"
                type="password"
                placeholder="Confirm new password"
                {...passwordForm.getInputProps('confirmPassword')}
                required
              />
              <Button type="submit" color="exportGreen" loading={loading}>
                Reset Password
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
    </Container>
  );
};